import OnboardingSheet from "../../OnboardingSheet";
import { Input } from "@/components/shared/Input/Input";
import { useTranslation } from "react-i18next";
import { useOnboardingAdressStep } from "../hooks/useOnboardingAdressStep";
import CustomSelect from "@/components/shared/CustomSelect/CustomSelect";
import { brasilStates, postalCodeMask } from "@/lib/select-options";
import CustomInputMask from "@/components/shared/CustomInputMask/CustomInputMask";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { SheetFooter, SheetTitle } from "@/components/ui/sheet";
import { CountryDropdown, type Country } from "@/components/ui/country-dropdown";
import { Label } from "@/components/ui/label";

interface AddressStepProps {
  open: boolean;
  onClose: () => void;
  isPending: boolean;
  isEditing?: boolean;
  canEdit?: boolean;
}

export function AddressStep({
  open,
  onClose,
  isPending,
  isEditing = false,
  canEdit = true,
}: AddressStepProps) {
  const { t } = useTranslation("onboarding");

  const {
    cep,
    setCep,
    register,
    handleSubmit,
    errors,
    defaultValues,
    onSubmit,
    handlerCep,
    isSendingAddress,
    isLoadingUserAddress,
    cepHelperText,
    setValue,
    isLoadingCep,
    watch,
  } = useOnboardingAdressStep({
    onClose,
    isOpen: open,
    mode: isEditing ? "edit" : "create",
  });

  const selectedCountry = (watch("country") || "BR").toUpperCase();
  const isBrazil = selectedCountry === "BR";
  const isReadOnly = isEditing && !canEdit;

  const handleCountryChange = (country: Country) => {
    const alpha2 = country.alpha2.toUpperCase();
    setValue("country", alpha2);
    setCep("");
    setValue("postal_code", "");
    setValue("state", "");
    setValue("city", "");
    setValue("district", "");
    setValue("street", "");
  };

  return (
    <OnboardingSheet
      onClose={onClose}
      open={open}
      isPending={isPending}
      hasHeader={!isEditing}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={`flex flex-col justify-between ${
          isEditing ? "h-full md:py-6 py-4" : "h-[calc(100vh-140px)]"
        }`}
      >
        <div className="p-6">
          <SheetTitle>{t("address")}</SheetTitle>

          <section className="max-sm:overflow-y-auto">
            <article className="mt-6 full flex flex-col justify-between">
              <div className="space-y-4">
                <div className="flex flex-col gap-2 w-full">
                  <Label>{t("country_label")}</Label>
                  <CountryDropdown
                    defaultValue={selectedCountry}
                    onChange={handleCountryChange}
                    placeholder={t("select_country_placeholder")}
                    disabled={isReadOnly}
                  />
                  {errors?.country?.message && (
                    <p className="text-sm text-error">
                      {String(errors.country.message)}
                    </p>
                  )}
                </div>

                {isBrazil ? (
                  <CustomInputMask
                    className="md:max-w-[220px]"
                    mask={(value) => postalCodeMask("brasil", value)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      setCep(value);

                      if (value.length === 8) {
                        handlerCep(value);
                      }
                    }}
                    value={
                      postalCodeMask(
                        "brasil",
                        defaultValues?.postal_code ?? cep
                      ) ?? ""
                    }
                    label={t("cep")}
                    placeholder={t("00000-000")}
                    type="text"
                    helperText={cepHelperText}
                    disabled={isReadOnly || isLoadingCep}
                    icon={
                      isLoadingCep ? (
                        <Loader2 className="animate-spin" />
                      ) : null
                    }
                  />
                ) : (
                  <Input
                    className="md:max-w-[220px]"
                    label={t("postal_code_label")}
                    placeholder={t("postal_code_placeholder")}
                    defaultValue={defaultValues.postal_code}
                    {...register("postal_code", {
                      setValueAs: (v) => v?.trim(),
                    })}
                    helperText={errors?.postal_code?.message}
                    disabled={isReadOnly}
                  />
                )}

                <>
                  <div className="flex gap-4 flex-col w-full md:flex-row">
                    <Input
                      className="w-full"
                      label={t("street")}
                      placeholder={t("street_placeholder")}
                      defaultValue={defaultValues.street}
                      {...register("street")}
                      helperText={errors?.street?.message}
                      disabled={isReadOnly}
                    />
                    <div className="md:w-[102px]">
                      <Input
                        {...register("number")}
                        label={t("number")}
                        helperText={errors?.number?.message}
                        placeholder={t("123")}
                        defaultValue={defaultValues.number}
                        type="number"
                        min={0}
                        disabled={isReadOnly}
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 flex-col w-full md:flex-row">
                    <Input
                      label={t("complement")}
                      placeholder={t("complement_placeholder")}
                      defaultValue={defaultValues.complement}
                      {...register("complement")}
                      disabled={isReadOnly}
                    />
                    <Input
                      {...register("district")}
                      label={isBrazil ? t("district") : t("district_optional_label")}
                      defaultValue={defaultValues.district}
                      placeholder={
                        isBrazil
                          ? t("district_placeholder")
                          : t("district_optional_placeholder")
                      }
                      helperText={errors?.district?.message}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="flex gap-4 flex-col w-full md:flex-row">
                    <Input
                      defaultValue={defaultValues?.city}
                      label={t("city")}
                      placeholder={t("city_placeholder")}
                      {...register("city")}
                      helperText={errors?.city?.message}
                      disabled={isReadOnly}
                    />
                    {isBrazil ? (
                      <CustomSelect
                        options={brasilStates}
                        label={t("state")}
                        placeholder={t("state_placeholder")}
                        value={watch("state")}
                        onValueChange={(value) => setValue("state", value)}
                        helperText={errors?.state?.message}
                        disabled={isReadOnly}
                      />
                    ) : (
                      <Input
                        label={t("state_region_label")}
                        placeholder={t("state_region_placeholder")}
                        defaultValue={defaultValues.state}
                        {...register("state")}
                        helperText={errors?.state?.message}
                        disabled={isReadOnly}
                      />
                    )}
                  </div>
                </>
              </div>
            </article>
          </section>
        </div>
        <SheetFooter className="flex gap-2 border-t border-t-colorBorder py-4 px-6 max-md:pb-24">
          <div className="flex justify-end items-center">
            <Button variant="ghost" onClick={onClose} type="button">
              {t("cancel")}
            </Button>
            <Button
              variant="default"
              type="submit"
              disabled={
                !canEdit || isLoadingUserAddress || isSendingAddress || isLoadingCep
              }
            >
              {isSendingAddress ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin" />
                  {t("saving")}
                </span>
              ) : (
                t("save")
              )}
            </Button>
          </div>
        </SheetFooter>
      </form>
    </OnboardingSheet>
  );
}